"use client";

/**
 * Statusband — Testleiste 2.0 (A6).
 * Zeigt nur den aktuellen Laufzustand, niemals die Reifegrad-Ampel.
 */

import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ampelClass, design, designClass } from "../design.ts";
import { formatElapsed } from "./status-modell.ts";
import type {
  StatusbandAktion,
  StatusbandSchrittPunkt,
  Testzustand,
} from "./status-modell.ts";

export type StatusbandProps = {
  prozessTitel: string;
  schrittTitel: string;
  schrittNummer: number;
  schrittGesamt: number;
  unternehmenName: string;
  zustand: Testzustand;
  zustandText: string;
  /** Laufende Sekunden — rechts neben den Aktionen, weiß. */
  elapsedSeconds?: number;
  verbindungUnterbrochen?: boolean;
  schritte: StatusbandSchrittPunkt[];
  aktionen: StatusbandAktion[];
  onSchrittKlick?: (punkt: StatusbandSchrittPunkt) => void;
  /** Technische Meldung hinter „Details". */
  fehlerTechnisch?: string | null;
  detailsOffen?: boolean;
  onDetailsToggle?: () => void;
  disabled?: boolean;
  className?: string;
};

function StatusIcon({ zustand }: { zustand: Testzustand }) {
  if (zustand === "laeuft" || zustand === "wartet_auf_dich") {
    return (
      <Loader2
        className="h-4 w-4 shrink-0 animate-spin text-[var(--status-warning-text)]"
        aria-hidden
      />
    );
  }
  if (zustand === "erfolgreich") {
    return (
      <span
        className={cn("h-4 w-4 shrink-0 rounded-full", ampelClass.gruen.dot)}
        aria-hidden
      />
    );
  }
  if (zustand === "fehlgeschlagen") {
    return (
      <span
        className={cn("h-4 w-4 shrink-0 rounded-full", ampelClass.rot.dot)}
        aria-hidden
      />
    );
  }
  return (
    <span
      className={cn("h-4 w-4 shrink-0 rounded-full", ampelClass.grau.dot)}
      aria-hidden
    />
  );
}

/**
 * 56 px Statusband, sticky, verschiebt Inhalt (im normalen Dokumentfluss).
 */
export function Statusband({
  prozessTitel,
  schrittTitel,
  schrittNummer,
  schrittGesamt,
  unternehmenName,
  zustand,
  zustandText,
  elapsedSeconds = 0,
  verbindungUnterbrochen = false,
  aktionen,
  fehlerTechnisch,
  detailsOffen,
  onDetailsToggle,
  disabled = false,
  className,
}: StatusbandProps) {
  const sichtbareAktionen = aktionen.slice(0, 3);
  const zeigeTimer =
    zustand === "laeuft" ||
    zustand === "wartet_auf_dich" ||
    zustand === "erfolgreich";

  return (
    <div
      className={cn(
        "sticky top-0 z-[90] w-full border-b border-[var(--border-default)] bg-[var(--surface-secondary)]",
        className,
      )}
      style={{ minHeight: design.statusband.hoehePx }}
      role="banner"
      aria-label="Teststatus"
      data-testid="dev-statusband"
      data-zustand={zustand}
    >
      <div
        className="flex items-center gap-3 px-3 py-1.5"
        style={{ minHeight: design.statusband.hoehePx }}
      >
        {/* Links — Titel + Status unter der Schrittzeile */}
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              designClass.sekundaer,
              "truncate font-medium text-[var(--text-primary)]",
            )}
          >
            {prozessTitel || "Test"}
            {schrittGesamt > 0 ? (
              <span className="font-normal text-[var(--text-secondary)]">
                {" "}
                · Schritt {schrittNummer} von {schrittGesamt}
              </span>
            ) : null}
          </p>
          <p
            className={cn(
              designClass.klein,
              "mt-0.5 truncate text-[var(--text-secondary)]",
            )}
          >
            {schrittTitel || "–"}
            {unternehmenName ? ` · ${unternehmenName}` : ""}
          </p>
          <div
            className="mt-1 flex min-w-0 items-center gap-2"
            role="status"
            aria-live="polite"
          >
            <StatusIcon zustand={zustand} />
            <p
              className={cn(
                designClass.klein,
                "min-w-0 truncate text-[var(--text-secondary)]",
              )}
              data-testid="dev-statusband-zustand"
            >
              {zustandText}
            </p>
          </div>
          {verbindungUnterbrochen ? (
            <p
              className={cn(
                designClass.klein,
                "mt-0.5 text-[var(--status-warning-text)]",
              )}
              role="status"
            >
              Verbindung unterbrochen — versuche erneut …
            </p>
          ) : null}
          {detailsOffen && fehlerTechnisch ? (
            <p
              className={cn(
                designClass.klein,
                "mt-1 max-h-16 overflow-auto text-left text-[var(--text-muted)]",
              )}
              data-testid="dev-statusband-details"
            >
              {fehlerTechnisch}
            </p>
          ) : null}
        </div>

        {/* Rechts — Timer (weiß) + Aktionen */}
        <div className="flex shrink-0 items-center gap-2">
          {zeigeTimer ? (
            <span
              className={cn(
                designClass.sekundaer,
                "tabular-nums text-white",
              )}
              data-testid="dev-statusband-timer"
              aria-label={`Vergangene Zeit ${formatElapsed(elapsedSeconds)}`}
            >
              {formatElapsed(elapsedSeconds)}
            </span>
          ) : null}
          {sichtbareAktionen.map((a) => (
            <Button
              key={a.id}
              type="button"
              size="sm"
              variant={a.primaer ? "default" : "outline"}
              className={cn(
                "h-7 px-2",
                designClass.klein,
                a.id === "details" && detailsOffen && "ring-1 ring-[var(--focus-ring)]",
              )}
              onClick={() => {
                if (a.id === "details" && onDetailsToggle) {
                  onDetailsToggle();
                  return;
                }
                a.onClick();
              }}
              disabled={disabled || Boolean(a.disabled)}
              data-testid={`dev-statusband-aktion-${a.id}`}
            >
              {a.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
